import { Injectable } from '@angular/core';
import {
  CanActivateFn,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { inject } from '@angular/core';
import { BrandService } from '../services/brand/brand.service';

/**
 * Route to Feature Mapping
 * Maps route paths to their corresponding feature flags
 */
const ROUTE_FEATURE_MAP: { [key: string]: string } = {
  'courier/pickup': 'courierPickup',
  'courier/delivery': 'courierDelivery',
  // NB: keep the more specific path listed too — getFeatureForRoute falls back
  // to a substring match, and 'customer/drop-multiple' contains 'customer/drop'.
  'customer/drop-multiple': 'customerDropMultiple',
  'customer/drop': 'customerDrop',
  'customer/collect': 'customerCollect',
  'parcel/putaway': 'putAway',
  'management/parcel-lookup': 'parcelLookup',
  'management/general-returns': 'generalReturns',
  'management/non-collection-return': 'nonCollectionReturn',
  'management/user-management': 'userManagement',
  'device-info': 'deviceInfo',
  'node-stock': 'nodeStock',
  'cache-checker': 'cacheChecker',
  'admin/cache': 'cacheManagement',
  'alerts/expired': 'expiredParcels',
  'alerts/unreceived': 'unreceivedParcels',
  'config': 'deviceConfig'
};

/**
 * Route Access Guard
 * Blocks access to routes when their corresponding feature is disabled
 * Can be applied to any route in app.routes.ts
 */
@Injectable({
  providedIn: 'root',
})
export class RouteAccessGuard {
  constructor(
    private brandService: BrandService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    const routePath = state.url.substring(1); // Remove leading slash

    // Config must remain reachable so users can set a DID before brand config is loaded.
    if (routePath === 'config') {
      return true;
    }

    const featureName = this.getFeatureForRoute(routePath);

    if (!featureName) {
      // No feature mapping for this route, allow access
      return true;
    }

    if (this.brandService.isFeatureEnabled(featureName)) {
      return true;
    }

    console.warn(`Route access denied: ${routePath} (feature ${featureName} disabled)`);
    this.router.navigate(['/menu']);
    return false;
  }

  /**
   * Map a route path to its feature name
   */
  private getFeatureForRoute(routePath: string): string | null {
    // Check exact matches first
    if (ROUTE_FEATURE_MAP[routePath]) {
      return ROUTE_FEATURE_MAP[routePath];
    }

    // Check partial matches
    for (const [route, feature] of Object.entries(ROUTE_FEATURE_MAP)) {
      if (routePath.includes(route)) {
        return feature;
      }
    }

    return null;
  }
}

/**
 * Functional route guard for use in standalone routes
 */
export const routeAccessGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const brandService = inject(BrandService);
  const router = inject(Router);
  const routePath = state.url.substring(1);

  if (routePath === 'config') {
    return true;
  }

  // Map route to feature
  let featureName: string | null = null;
  if (ROUTE_FEATURE_MAP[routePath]) {
    featureName = ROUTE_FEATURE_MAP[routePath];
  } else {
    for (const [route, feature] of Object.entries(ROUTE_FEATURE_MAP)) {
      if (routePath.includes(route)) {
        featureName = feature;
        break;
      }
    }
  }

  if (!featureName) {
    // No feature mapping, allow access
    return true;
  }

  if (brandService.isFeatureEnabled(featureName)) {
    return true;
  }

  console.warn(`Route access denied: ${routePath} (feature ${featureName} disabled)`);
  router.navigate(['/menu']);
  return false;
};
